import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { company } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "What We Do" },
  { to: "/partners", label: "Partners" },
  { to: "/insights", label: "Industry Insights" },
  { to: "/revenue-ops", label: "Revenue Ops" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#4a7b9d]/95 backdrop-blur-md shadow-lg"
          : "bg-[#4a7b9d]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="bg-white rounded-xl p-1.5 shadow-md group-hover:shadow-xl transition-shadow duration-300 logo-entrance logo-glow">
              <img
                src={company.logo}
                alt={company.name}
                width="56"
                height="56"
                className="h-12 lg:h-14 w-auto"
                decoding="async"
              />
            </div>
            <div className="hidden sm:block leading-tight max-w-[260px] lg:max-w-[340px]">
              <div className="text-white font-display text-lg lg:text-xl">
                Creative Sales Source
              </div>
              <div className="text-white/80 text-[10px] lg:text-[11px] tracking-[0.08em] uppercase mt-0.5">
                The Trusted Connection Between Building Manufacturers & Dealers
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 ${
                    isActive
                      ? "text-white"
                      : "text-white/80 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute left-4 right-4 -bottom-0.5 h-0.5 bg-white rounded-full transition-all duration-300 ${
                        isActive ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
            <Link to="/contact" className="ml-3">
              <Button className="bg-[#4a6c7b] hover:bg-[#3d5a68] text-white rounded-md px-5 h-10 shadow-md hover:shadow-lg transition-all">
                Let's Talk
              </Button>
            </Link>
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2 rounded-md hover:bg-white/10 transition"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile nav */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-out ${
            open ? "max-h-96 pb-6" : "max-h-0"
          }`}
        >
          <nav className="flex flex-col gap-1 pt-2">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive
                      ? "bg-white/15 text-white"
                      : "text-white/85 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter/subscribe", { email, source: "footer" });
      toast({
        title: "You're on the list",
        description: `We'll send the quarterly Field Report to ${email}.`,
      });
      setEmail("");
    } catch (err) {
      toast({
        title: "Couldn't subscribe",
        description: err?.response?.data?.detail || "Please check the address and try again.",
      });
    }
  };

  return (
    <footer className="bg-[#3d5a68] text-white">
      {/* Newsletter strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">
                Stay Connected
              </h3>
              <p className="mt-3 text-white/75 max-w-md">
                Quarterly notes from the field — market trends, distributor news, and product launches across the Midwest.
              </p>
            </div>
            <form onSubmit={onSubscribe} className="flex flex-col sm:flex-row gap-3">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12 focus-visible:ring-white/40"
              />
              <Button
                type="submit"
                className="bg-white text-[#3d5a68] hover:bg-white/90 h-12 px-7 font-semibold"
              >
                Sign Up
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-white rounded-lg p-1.5">
              <img src={company.logo} alt={company.name} className="h-10 w-auto" />
            </div>
            <span className="font-display text-lg">Creative Sales Source</span>
          </div>
          <p className="text-white/70 text-sm leading-relaxed">
          An outsourced sales force for building-materials manufacturers — growing market share across the Midwest since 2012.
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-white uppercase text-xs tracking-[0.18em] mb-4">Explore</h4>
          <ul className="space-y-2 text-white/75 text-sm">
            {navLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-white transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-white uppercase text-xs tracking-[0.18em] mb-4">Contact</h4>
          <ul className="space-y-3 text-white/75 text-sm">
            <li className="flex items-start gap-2">
              <MapPin size={16} className="mt-0.5 flex-shrink-0" />
              <span>{company.address}</span>
            </li>
            <li className="flex items-center gap-2">
              <Phone size={16} />
              <a href={`tel:${company.phone}`} className="hover:text-white">{company.phone}</a>
            </li>
            <li className="flex items-center gap-2">
              <Mail size={16} />
              <a href={`mailto:${company.email}`} className="hover:text-white">{company.email}</a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-white uppercase text-xs tracking-[0.18em] mb-4">Follow</h4>
          <div className="flex gap-3">
            <a href={company.socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook"
               className="w-10 h-10 rounded-full bg-white/10 hover:bg-white hover:text-[#3d5a68] flex items-center justify-center transition-all">
              <Facebook size={18} />
            </a>
            <a href={company.socials.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"
               className="w-10 h-10 rounded-full bg-white/10 hover:bg-white hover:text-[#3d5a68] flex items-center justify-center transition-all">
              <Instagram size={18} />
            </a>
            <a href={company.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"
               className="w-10 h-10 rounded-full bg-white/10 hover:bg-white hover:text-[#3d5a68] flex items-center justify-center transition-all">
              <Linkedin size={18} />
            </a>
            <a href={company.socials.x} target="_blank" rel="noreferrer" aria-label="X"
               className="w-10 h-10 rounded-full bg-white/10 hover:bg-white hover:text-[#3d5a68] flex items-center justify-center transition-all">
              <span className="font-bold text-sm">X</span>
            </a>
          </div>
          <p className="mt-6 text-white/60 text-xs leading-relaxed">{company.hours}</p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/60">
          <p>© {new Date().getFullYear()} Creative Sales Source LLC — All Rights Reserved.</p>
          <p>Growing sales from Indiana through Montana.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#eef3f7]">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
